import React, { useMemo } from "react";
import {
  TwilioConversationModel,
  WebChatBotConversationModel,
  CRMConversationAutoReplyStatus,
  InstagramConversationModel,
  WhatsappConversationModel,
  CRMConversationAutoResponseStatus,
} from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { ReactComponent as AgentIcon } from "./assets/agent.svg";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as AttentionIcon } from "./assets/attention.svg";

type CRMAgentInfoProps = {
  conversation:
    | WhatsappConversationModel
    | InstagramConversationModel
    | WebChatBotConversationModel
    | TwilioConversationModel;
};

export const CRMAgentInfo: React.FC<CRMAgentInfoProps> = ({ conversation }) => {
  const { t } = useTranslations();

  const aiAgentInfo = useMemo(() => {
    const {
      auto_reply: autoReply,
      auto_response: autoResponse,
      attended_by: attendedBy,
    } = conversation;

    const isAutoReplyDefined = typeof autoReply === "number";

    const isAIActive =
      (!isAutoReplyDefined &&
        !attendedBy &&
        autoResponse === CRMConversationAutoResponseStatus.active) ||
      autoReply === CRMConversationAutoReplyStatus.active;

    if (isAIActive) {
      return {
        label: t({
          id: "client-support-page.ai-agent",
          defaultMessage: "AI Agent",
        }),
        icon: <AIIcon />,
      };
    }

    if (autoReply === CRMConversationAutoReplyStatus.handedOver) {
      return {
        label: t({
          id: "client-support-page.requires-manual-response",
          defaultMessage: "Requires manual response",
        }),
        icon: <AttentionIcon />,
      };
    }

    return (!isAutoReplyDefined ||
      autoReply === CRMConversationAutoReplyStatus.inactive) &&
      attendedBy
      ? {
          label: attendedBy,
          icon: <AgentIcon />,
        }
      : null;
  }, [conversation, t]);

  if (!aiAgentInfo) return null;

  return (
    <div className="flex items-center gap-1">
      <div className="flex shrink-0 items-center justify-center">
        {aiAgentInfo.icon}
      </div>
      <span className="text-primary-800 truncate text-sm leading-[16px]">
        {aiAgentInfo.label}
      </span>
    </div>
  );
};
